<template>
  <default-layout :breadcrumbs="breadcrumbs">
    <main class="featured-agent-learn">
      <section class="featured-agent-hero">
        <div class="featured-agent-hero--copy">
          <div class="hero-kicker">
            <span>Agen Pilihan</span>
          </div>

          <h1>Jadi Agen Pilihan di Area Terbaik Anda</h1>

          <p>
            Tingkatkan visibilitas profil dan listing Anda di area spesifik yang Anda pilih. Profil
            Anda berpeluang tampil di halaman pencarian area, sehingga calon buyer lebih mudah
            mengenal Anda dan melihat listing yang Anda pasarkan.
          </p>

          <div class="hero-actions">
            <router-link class="btn hero-primary" to="/layanan-agen/agen-properti-pilihan/beli">
              <span>Beli Slot Agen Pilihan</span>
              <v-icon>mdi-arrow-right</v-icon>
            </router-link>

            <div class="hero-note">Masa aktif 1-12 bulan per area spesifik.</div>
          </div>
        </div>

        <div
          class="featured-agent-hero--visual hide--in-mobile hide--in-tablet"
          aria-hidden="true"
        ></div>
      </section>

      <section class="featured-agent-section">
        <div class="section-heading">
          <h2>Kenapa Jadi Agen Pilihan?</h2>
        </div>

        <div class="benefit-grid">
          <article v-for="benefit in benefits" :key="benefit.title" class="benefit-card">
            <div class="benefit-card--icon">
              <v-icon>{{ benefit.icon }}</v-icon>
            </div>

            <h3>{{ benefit.title }}</h3>
            <p>{{ benefit.description }}</p>
          </article>
        </div>
      </section>

      <section class="slot-panel">
        <div class="slot-panel--copy">
          <span>Slot Terbatas</span>

          <h2>Maksimal 10 agen per area spesifik</h2>

          <p>
            Anda dapat memilih masa aktif mulai dari <b>1 bulan hingga 12 bulan</b>, sesuai
            kebutuhan promosi Anda.
          </p>
        </div>

        <div class="slot-avatar-board" aria-hidden="true">
          <div
            v-for="(agent, index) in indonesianAgents"
            :key="agent.id"
            class="slot-avatar-card"
            :ref="'avatar-' + index"
          >
            <img :src="agent.avatar" :alt="agent.name" />
          </div>
        </div>
      </section>

      <section class="featured-agent-section">
        <div class="section-heading">
          <h2>Cara Membeli Slot Agen Pilihan</h2>
        </div>

        <div class="steps-list">
          <article v-for="(step, index) in steps" :key="step" class="step-item">
            <div class="step-number">{{ index + 1 }}</div>
            <p>{{ step }}</p>
          </article>
        </div>
      </section>

      <section class="featured-agent-cta">
        <div>
          <span>Agen Pilihan</span>

          <h2>Saatnya Tampil Lebih Menonjol</h2>

          <p>
            Pilih area terbaik Anda dan jadilah salah satu Agen Pilihan agar profil dan listing Anda
            lebih mudah ditemukan calon buyer.
          </p>
        </div>

        <router-link
          class="btn featured-agent-cta--button"
          to="/layanan-agen/agen-properti-pilihan/beli"
        >
          <span>Beli Slot Agen Pilihan</span>
          <v-icon>mdi-arrow-right</v-icon>
        </router-link>
      </section>
    </main>
  </default-layout>
</template>

<script>
import DefaultLayout from '@/layouts/default';
import DefaultHeadMixin from '@/mixins/defaultHead';

const SLOT_POSITIONS = ['10%', '30%', '50%', '70%', '90%'];
const SLOT_SCALES = [0.88, 0.96, 1.12, 0.96, 0.88];
const SLOT_OPACITIES = [0.65, 0.82, 1, 0.82, 0.65];
const SLOT_ZINDEXES = [1, 2, 5, 2, 1];

export default {
  name: 'featured-agent-cara-kerja',

  mixins: [DefaultHeadMixin],
  async fetch({ store }) {
    const url = 'https://agen.belirumah.co/layanan-agen/agen-properti-pilihan/cara-kerja';
    const title = 'Agen Properti Pilihan | Tingkatkan Visibilitas Listing Anda | Agen BeliRumah.co';
    const description =
      'Jadi Agen Properti Pilihan di area terbaik Anda. Tingkatkan visibilitas profil dan listing agar lebih mudah ditemukan oleh calon buyer';

    store.commit('head/SET_CONTENT', {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'og-url', property: 'og:url', content: url },
        { hid: 'og-type', property: 'og:type', content: 'website' },
        { hid: 'og-description', property: 'og:description', content: description },
      ],
      link: [{ rel: 'canonical', href: url, id: 'canonical' }],
    });
  },

  components: {
    DefaultLayout,
  },

  data() {
    return {
      avatarOffset: 0,
      avatarTimer: null,
    };
  },

  computed: {
    breadcrumbs() {
      if (this.$route.query.mobile === 'true') return [];

      return [
        {
          text: this.$t('breadcrumb.homepage'),
          to: '/',
        },
        {
          text: 'Agen Pilihan',
          to: '/layanan-agen/agen-properti-pilihan',
        },
        {
          text: 'Cara Kerja',
          to: this.$router.currentRoute.path,
        },
      ];
    },

    benefits() {
      return [
        {
          icon: 'mdi-map-marker-star-outline',
          title: 'Tampil di Area Pilihan',
          description: 'Foto profil Anda akan muncul di halaman pencarian area yang Anda pilih.',
        },
        {
          icon: 'mdi-file-star-outline',
          title: 'Listing Lebih Menonjol',
          description:
            'Listing Anda di area tersebut mendapatkan tanda khusus Agen Pilihan agar lebih mudah dibedakan.',
        },
        {
          icon: 'mdi-chart-line',
          title: 'Eksposur Lebih Maksimal',
          description:
            'Setiap halaman pencarian area menampilkan 5 Agen Pilihan secara acak dari slot aktif.',
        },
        {
          icon: 'mdi-flag-variant-outline',
          title: 'Banner Spesial di Listing',
          description:
            'Listing milik Agen Pilihan mendapatkan banner khusus untuk meningkatkan perhatian calon buyer.',
        },
      ];
    },

    steps() {
      return [
        'Buka menu Akun di website atau aplikasi Agen BeliRumah.',
        'Pilih menu Agen Pilihan.',
        'Klik tombol + Beli Slot Agen Pilihan.',
        'Pilih Provinsi, Kota/Kabupaten, dan Area yang diinginkan.',
        'Cek informasi bulan aktif dan sisa slot tersedia.',
        'Pilih durasi Agen Pilihan.',
        'Klik + Beli Slot Agen Pilihan.',
        'Periksa detail pembelian dan jumlah koin yang dibutuhkan.',
        'Klik Beli Sekarang untuk mengaktifkan slot Anda.',
      ];
    },

    indonesianAgents() {
      return [
        {
          id: 'agent-1',
          name: 'Agent 1',
          avatar: require('@/assets/img/person/1.jpg'),
        },
        {
          id: 'agent-2',
          name: 'Agent 2',
          avatar: require('@/assets/img/person/2.jpg'),
        },
        {
          id: 'agent-3',
          name: 'Agent 3',
          avatar: require('@/assets/img/person/3.jpg'),
        },
        {
          id: 'agent-4',
          name: 'Agent 4',
          avatar: require('@/assets/img/person/4.jpg'),
        },
        {
          id: 'agent-5',
          name: 'Agent 5',
          avatar: require('@/assets/img/person/5.jpg'),
        },
        {
          id: 'agent-6',
          name: 'Agent 6',
          avatar: require('@/assets/img/person/6.jpg'),
        },
      ];
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.applyAvatarPositions(false);

      window.setTimeout(() => {
        this.avatarTimer = window.setInterval(this.stepCarousel, 2200);
      }, 150);
    });
  },

  beforeDestroy() {
    if (this.avatarTimer) {
      window.clearInterval(this.avatarTimer);
    }
  },

  methods: {
    stepCarousel() {
      if (!this.indonesianAgents.length) return;

      this.avatarOffset = (this.avatarOffset + 1) % this.indonesianAgents.length;
      this.applyAvatarPositions(true);
    },

    applyAvatarPositions(withTransition) {
      const totalAvatars = this.indonesianAgents.length;
      const totalSlots = SLOT_POSITIONS.length;

      for (let i = 0; i < totalAvatars; i++) {
        const cards = this.$refs[`avatar-${i}`];
        const card = Array.isArray(cards) ? cards[0] : cards;

        if (!card) continue;

        const slotIndex = (i - this.avatarOffset + totalAvatars) % totalAvatars;
        const isVisible = slotIndex < totalSlots;

        card.style.transition = withTransition
          ? 'left 0.75s cubic-bezier(0.4, 0, 0.2, 1), transform 0.75s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.55s ease'
          : 'none';

        if (isVisible) {
          card.style.left = SLOT_POSITIONS[slotIndex];
          card.style.transform = `translate(-50%, -50%) scale(${SLOT_SCALES[slotIndex]})`;
          card.style.opacity = SLOT_OPACITIES[slotIndex];
          card.style.zIndex = SLOT_ZINDEXES[slotIndex];
          card.style.pointerEvents = 'auto';
        } else {
          card.style.left = slotIndex === totalSlots ? '110%' : '-10%';
          card.style.transform = 'translate(-50%, -50%) scale(0.82)';
          card.style.opacity = '0';
          card.style.zIndex = '0';
          card.style.pointerEvents = 'none';
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/utils/variables';

.featured-agent-learn {
  --fa-red: #d71920;
  --fa-ink: #17191d;
  --fa-muted: #65717f;
  --fa-line: #ece7e7;
  --fa-soft: #fff6f4;
  --fa-shadow: 0 18px 48px rgba(19, 24, 31, 0.08);

  min-height: 100vh;
  padding: 0 0 76px;
}

/* ─── HERO ───────────────────────────────────────────────── */
.featured-agent-hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 0.75fr);
  gap: 34px;
  align-items: center;
  max-width: 1180px;
  min-height: 520px;
  margin: 0 auto;
  padding: 54px 28px 42px;
  overflow: hidden;
}

.featured-agent-hero--copy {
  position: relative;
  z-index: 1;
  max-width: 680px;

  h1 {
    max-width: 650px;
    margin: 20px 0 0;
    color: var(--fa-ink);
    font-family: Poppins-Bold, sans-serif;
    font-size: 54px;
    line-height: 1.08;
  }

  p {
    max-width: 630px;
    margin: 22px 0 0;
    color: #4f5965;
    font-family: Poppins, sans-serif;
    font-size: 16px;
    line-height: 1.85;
  }
}

.hero-kicker {
  display: flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  padding: 7px;
  border: 1px solid #eadfe0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 10px 28px rgba(19, 24, 31, 0.06);

  span {
    padding: 7px 12px;
    border-radius: 999px;
    background: var(--fa-red);
    color: #ffffff;
    font-family: Poppins-Bold, sans-serif;
    font-size: 12px;
  }

  strong {
    padding-right: 10px;
    color: #3a414a;
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 12px;
  }
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 30px;
}

.hero-primary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 22px;
  border-radius: 14px;
  background: var(--fa-red);
  color: #ffffff;
  font-family: Poppins-Bold, sans-serif;
  box-shadow: 0 14px 28px rgba(215, 25, 32, 0.2);

  .v-icon {
    color: #ffffff;
    font-size: 18px;
  }
}

.hero-note {
  max-width: 220px;
  color: var(--fa-muted);
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 12px;
  line-height: 1.5;
}

.featured-agent-hero--visual {
  position: relative;
  min-height: 480px;
  border-radius: 34px;
  background: linear-gradient(rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0.45)),
    url('~@/assets/img/background-agen.jpg') center / cover no-repeat;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -2px;
    width: min(92%, 430px);
    height: 104%;
    background: url('~@/assets/img/person-agen-pilihan.png') no-repeat bottom center;
    background-size: contain;
    transform: translateX(-50%);
    filter: drop-shadow(0 26px 34px rgba(19, 24, 31, 0.24));
  }
}

/* ─── SHARED SECTION WRAPPER ────────────────────────────── */
.featured-agent-section,
.slot-panel,
.featured-agent-cta {
  max-width: 1180px;
  margin: 64px auto 0;
  padding: 0 24px;
}

/* ─── SECTION HEADING ───────────────────────────────────── */
.section-heading {
  position: relative;
  max-width: 760px;
  margin-bottom: 24px;
  padding-left: 22px;

  h2 {
    max-width: 640px;
    margin: 0;
    color: var(--fa-ink);
    font-family: Poppins-Bold, sans-serif;
    font-size: 34px;
    line-height: 1.18;
  }

  &::before {
    content: '';
    position: absolute;
    top: 5px;
    bottom: 5px;
    left: 0;
    width: 4px;
    border-radius: 999px;
    background: var(--fa-red);
  }
}

/* ─── BENEFIT GRID ──────────────────────────────────────── */
.benefit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.benefit-card {
  position: relative;
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  column-gap: 18px;
  min-height: 170px;
  padding: 22px 24px;
  border: 1px solid var(--fa-line);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 12px 34px rgba(19, 24, 31, 0.045);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: var(--fa-red);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    transform: translateY(-2px);

    &::after {
      opacity: 1;
    }
  }

  h3 {
    grid-column: 2;
    margin: 0 0 8px;
    color: var(--fa-ink);
    font-family: Poppins-Bold, sans-serif;
    font-size: 18px;
    line-height: 1.35;
  }

  p {
    grid-column: 2;
    margin: 0;
    color: var(--fa-muted);
    font-family: Poppins, sans-serif;
    font-size: 13.5px;
    line-height: 1.7;
  }
}

.benefit-card--index {
  position: absolute;
  top: 18px;
  right: 20px;
  color: #eee7e7;
  font-family: Poppins-Bold, sans-serif;
  font-size: 28px;
}

.benefit-card--icon {
  grid-row: 1 / span 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border: 1px solid #f3d5d5;
  border-radius: 16px;
  background: var(--fa-soft);
  color: var(--fa-red);

  .v-icon {
    color: var(--fa-red);
    font-size: 25px;
  }
}

/* ─── SLOT PANEL ────────────────────────────────────────── */
.slot-panel {
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(360px, 1fr);
  gap: 38px;
  align-items: center;
  padding: 34px;
  border: 1px solid #eadfe0;
  border-radius: 24px;
  background: linear-gradient(135deg, #ffffff 0%, #fff7f6 100%);
  box-shadow: var(--fa-shadow);

  span {
    color: var(--fa-red);
    font-family: Poppins-Bold, sans-serif;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  h2 {
    margin: 8px 0 0;
    color: var(--fa-ink);
    font-family: Poppins-Bold, sans-serif;
    font-size: 32px;
    line-height: 1.18;
  }

  p {
    margin: 14px 0 0;
    color: var(--fa-muted);
    font-family: Poppins, sans-serif;
    font-size: 15px;
    line-height: 1.7;
  }
}

/* ─── AVATAR BOARD ──────────────────────────────────────── */
.slot-avatar-board {
  position: relative;
  min-height: 132px;
  width: 100%;
  box-sizing: border-box;
  padding: 28px 20px;
  border: 1px solid #eee2e2;
  border-radius: 22px;
  background: radial-gradient(circle at 20% 20%, rgba(215, 25, 32, 0.08), transparent 32%), #ffffff;
  overflow: hidden;
}

.slot-avatar-card {
  position: absolute;
  top: 50%;
  width: 64px;
  height: 64px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  background: #f4f5f6;
  box-shadow: 0 10px 22px rgba(19, 24, 31, 0.14);
  overflow: hidden;
  will-change: left, transform, opacity;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

/* ─── STEPS ─────────────────────────────────────────────── */
.steps-list {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  max-width: 900px;
  margin-left: 220px;
  border-left: 2px solid #eee4e4;
}

.step-item {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  padding: 0 0 22px 28px;
  transition: transform 0.2s ease;

  &:last-child {
    padding-bottom: 0;
  }

  &:hover {
    transform: translateY(-2px);

    .step-number {
      background: var(--fa-red);
      box-shadow: 0 10px 22px rgba(215, 25, 32, 0.24);
    }

    p {
      border-color: #f0c9cb;
      color: var(--fa-ink);
    }
  }

  p {
    margin: 0;
    padding: 18px 20px;
    border: 1px solid var(--fa-line);
    border-radius: 16px;
    background: #ffffff;
    color: #333a42;
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    box-shadow: 0 10px 28px rgba(19, 24, 31, 0.04);
    transition: border-color 0.2s ease, color 0.2s ease;
  }
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin-left: -50px;
  border: 4px solid #ffffff;
  border-radius: 50%;
  background: var(--fa-ink);
  color: #ffffff;
  font-family: Poppins-Bold, sans-serif;
  font-size: 12px;
  box-shadow: 0 8px 18px rgba(19, 24, 31, 0.16);
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

/* ─── CTA ───────────────────────────────────────────────── */
.featured-agent-cta {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
  padding: 34px 38px;
  border-radius: 26px;
  background: linear-gradient(120deg, rgba(23, 25, 29, 0.96), rgba(23, 25, 29, 0.86)),
    url('~@/assets/img/gedung.jpg');
  background-size: cover;
  background-position: center;
  color: #ffffff;
  overflow: hidden;

  span {
    color: #ffdddd;
    font-family: Poppins-Bold, sans-serif;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  h2 {
    margin: 8px 0 10px;
    font-family: Poppins-Bold, sans-serif;
    font-size: 32px;
    line-height: 1.2;
  }

  p {
    max-width: 660px;
    margin: 0;
    color: rgba(255, 255, 255, 0.78);
    font-family: Poppins, sans-serif;
    font-size: 14px;
    line-height: 1.7;
  }
}

.featured-agent-cta--button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: max-content;
  height: 48px;
  padding: 0 24px;
  border-radius: 14px;
  background: #ffffff;
  color: var(--fa-red);
  font-family: Poppins-Bold, sans-serif;

  span {
    color: var(--fa-red);
    letter-spacing: 0;
    text-transform: none;
    font-size: 14px;
  }

  .v-icon {
    color: var(--fa-red);
    font-size: 18px;
  }
}

/* ─── HERO ENTRANCE ANIMATION ───────────────────────────── */
@media (prefers-reduced-motion: no-preference) {
  .featured-agent-hero--visual {
    animation: spotlight-panel-in 0.55s ease-out both;
  }
}

@keyframes spotlight-panel-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ─── RESPONSIVE ─────────────────────────────────────────── */
@media (max-width: 1024px) {
  .featured-agent-hero {
    grid-template-columns: 1fr;
    gap: 22px;
  }

  .featured-agent-hero--copy {
    h1 {
      font-size: 46px;
    }
  }

  .featured-agent-hero--visual {
    max-width: 620px;
  }

  .steps-list {
    margin-left: 26px;
  }

  .slot-panel {
    width: calc(100% - 48px);
    grid-template-columns: 1fr;
    gap: 24px;
    margin-right: auto;
    margin-left: auto;
    padding: 30px;
  }

  .slot-panel--copy {
    max-width: 640px;
  }

  .slot-avatar-board {
    min-height: 128px;
    padding: 26px 18px;
  }

  .featured-agent-cta {
    width: calc(100% - 48px);
    flex-direction: column;
    align-items: flex-start;
    margin-right: auto;
    margin-left: auto;
    padding: 30px;
  }

  .featured-agent-cta--button {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .featured-agent-learn {
    padding-bottom: 48px;
  }

  .featured-agent-hero {
    min-height: unset;
    padding: 28px 18px 20px;
  }

  .featured-agent-hero--copy {
    h1 {
      font-size: 34px;
    }

    p {
      font-size: 14px;
      line-height: 1.7;
    }
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-primary {
    justify-content: center;
    width: 100%;
  }

  .hero-note {
    max-width: none;
  }

  .featured-agent-hero--visual {
    min-height: 380px;
  }

  .featured-agent-section,
  .slot-panel,
  .featured-agent-cta {
    margin-top: 40px;
  }

  .featured-agent-section {
    padding-right: 18px;
    padding-left: 18px;
  }

  .benefit-grid,
  .slot-panel {
    grid-template-columns: 1fr;
  }

  .benefit-card {
    grid-template-columns: 46px minmax(0, 1fr);
    min-height: unset;
    padding: 18px;
  }

  .benefit-card--icon {
    width: 44px;
    height: 44px;
  }

  .slot-panel,
  .featured-agent-cta {
    width: calc(100% - 32px);
    margin-right: auto;
    margin-left: auto;
    padding: 24px 20px;
  }

  .slot-panel {
    gap: 20px;
    border-radius: 20px;

    span {
      font-size: 10.5px;
    }

    h2 {
      font-size: 25px;
      line-height: 1.22;
    }

    p {
      font-size: 14px;
      line-height: 1.65;
    }
  }

  .slot-avatar-board {
    min-height: 108px;
    padding: 22px 12px;
    border-radius: 18px;
  }

  .slot-avatar-card {
    width: 54px;
    height: 54px;
    border-width: 2px;
  }

  .steps-list {
    margin-left: 20px;
  }

  .step-item {
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 12px;
    padding-left: 24px;
  }

  .step-number {
    width: 38px;
    height: 38px;
    margin-left: -44px;
  }

  .featured-agent-cta {
    flex-direction: column;
    align-items: flex-start;
  }

  .featured-agent-cta--button {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 420px) {
  .hero-kicker {
    max-width: 100%;
  }

  .benefit-card {
    grid-template-columns: 1fr;
  }

  .benefit-card--icon,
  .benefit-card h3,
  .benefit-card p {
    grid-column: 1;
  }

  .slot-avatar-card {
    width: 48px;
    height: 48px;
  }

  .slot-avatar-board {
    min-height: 96px;
    padding-right: 8px;
    padding-left: 8px;
  }
}
</style>
